import { createContext, useContext, useEffect, useState, useRef, type ReactNode } from 'react';
import type { Session, AuthError } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import type { User, UserRole } from '../types';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  error: string | null;
  signIn: (email: string, password: string) => Promise<{ error: AuthError | null }>;
  signOut: () => Promise<void>;
  clearError: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const initializedRef = useRef(false);
  // Para rastrear si ya cargamos el perfil de esta sesión
  const profileLoadedForSession = useRef<string | null>(null);

  // Obtener perfil del usuario desde la tabla profiles
  const fetchUserProfile = async (userId: string): Promise<User | null> => {
    try {
      console.log('🔐 Consultando perfil para userId:', userId);
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();

      if (error) {
        console.error('🔐 Error al obtener perfil:', error);
        return null;
      }

      console.log('🔐 Perfil obtenido:', data);
      return {
        id: data.id,
        email: data.email,
        nombre: data.nombre,
        rol: data.rol as UserRole,
        activo: data.activo,
        created_at: data.created_at,
        updated_at: data.updated_at,
      };
    } catch (err) {
      console.error('🔐 Error inesperado al obtener perfil:', err);
      return null;
    }
  };

  // Efecto para cargar el perfil cuando cambia la sesión
  useEffect(() => {
    const loadProfile = async () => {
      if (session?.user && profileLoadedForSession.current !== session.user.id) {
        console.log('🔐 Cargando perfil para sesión:', session.user.id);
        profileLoadedForSession.current = session.user.id;

        const profile = await fetchUserProfile(session.user.id);
        if (profile) {
          console.log('🔐 Perfil cargado exitosamente:', profile.nombre);
          setUser(profile);
        } else {
          console.log('🔐 No se pudo cargar el perfil');
          // Si no hay perfil, cerrar sesión
          setSession(null);
          profileLoadedForSession.current = null;
        }
        setLoading(false);
      }
    };

    loadProfile();
  }, [session]);

  // Inicializar autenticación solo una vez
  useEffect(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;

    console.log('🔐 Inicializando auth listener...');

    // Escuchar cambios de autenticación
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, newSession) => {
        console.log('🔐 Auth state changed:', event, newSession?.user?.id || 'no user');

        if (event === 'INITIAL_SESSION') {
          // Sesión restaurada del storage
          if (newSession) {
            console.log('🔐 Sesión inicial encontrada');
            setSession(newSession);
          } else {
            console.log('🔐 No hay sesión inicial');
            setLoading(false);
          }
        } else if (event === 'SIGNED_IN') {
          console.log('🔐 Usuario inició sesión');
          setSession(newSession);
        } else if (event === 'SIGNED_OUT') {
          console.log('🔐 Usuario cerró sesión');
          setUser(null);
          setSession(null);
          profileLoadedForSession.current = null;
          setLoading(false);
        } else if (event === 'TOKEN_REFRESHED' && newSession) {
          console.log('🔐 Token refrescado');
          setSession(newSession);
        }
      }
    );

    // Timeout de seguridad - si después de 8 segundos no se ha resuelto
    const timeoutId = setTimeout(() => {
      if (loading) {
        console.log('🔐 Timeout de seguridad - finalizando loading');
        setLoading(false);
      }
    }, 8000);

    return () => {
      clearTimeout(timeoutId);
      subscription.unsubscribe();
    };
  }, [loading]);

  // Iniciar sesión con timeout
  const signIn = async (email: string, password: string) => {
    setLoading(true);
    setError(null);

    try {
      console.log('🔐 Intentando login con:', email);

      // Crear timeout de 10 segundos
      const timeoutPromise = new Promise<never>((_, reject) => {
        setTimeout(() => reject(new Error('Timeout: La conexión tardó demasiado')), 10000);
      });

      const signInPromise = supabase.auth.signInWithPassword({ email, password });

      const { data, error: signInError } = await Promise.race([signInPromise, timeoutPromise]);

      if (signInError) {
        console.error('🔐 Error de login:', signInError);
        setError(signInError.message);
        return { error: signInError };
      }

      console.log('🔐 Login exitoso:', data.user?.email);

      if (data.user) {
        const profile = await fetchUserProfile(data.user.id);

        if (!profile) {
          setError('No se encontró el perfil del usuario');
          await supabase.auth.signOut();
          return { error: { message: 'Perfil no encontrado' } as AuthError };
        }

        if (!profile.activo) {
          setError('Usuario desactivado. Contacte al administrador.');
          await supabase.auth.signOut();
          return { error: { message: 'Usuario inactivo' } as AuthError };
        }

        setUser(profile);
        setSession(data.session);
      }

      return { error: null };
    } catch (err) {
      let errorMessage = err instanceof Error ? err.message : 'Error desconocido';
      console.error('🔐 Error en signIn:', errorMessage);

      // Traducir errores comunes a mensajes más claros
      if (errorMessage.includes('aborted') || errorMessage.includes('signal')) {
        errorMessage = 'Error de conexión. Por favor recarga la página e intenta de nuevo.';
      } else if (errorMessage.includes('Timeout')) {
        errorMessage = 'La conexión tardó demasiado. Verifica tu internet e intenta de nuevo.';
      } else if (errorMessage.includes('fetch') || errorMessage.includes('network')) {
        errorMessage = 'Error de red. Verifica tu conexión a internet.';
      }

      setError(errorMessage);
      return { error: { message: errorMessage } as AuthError };
    } finally {
      setLoading(false);
    }
  };

  // Cerrar sesión
  const signOut = async () => {
    setLoading(true);
    try {
      await supabase.auth.signOut();
      setUser(null);
      setSession(null);
    } catch (err) {
      console.error('Error al cerrar sesión:', err);
    } finally {
      setLoading(false);
    }
  };

  // Limpiar error
  const clearError = () => setError(null);

  const value: AuthContextType = {
    user,
    session,
    loading,
    error,
    signIn,
    signOut,
    clearError,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// Hook para usar el contexto
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth debe usarse dentro de un AuthProvider');
  }
  return context;
}
